import {
  Translate,
  AlignParameters,
  Scale,
  _Scale,
} from '../../../../../elements/resource/types/shape.type';
import { Container, Sprite, Texture } from 'pixi.js';
import {
  StudioAnimation,
  StudioCell,
  StudioImageShapeDescriptor,
} from '../../studio.types';
import { selectMedia } from '../../studio.actions';
import { DragResponse } from '../../../../../elements/util/point-controller/point-controller';
import { RectangleController } from '../../../../../elements/util/rectangle-controller/rectangle-controller';
import { StudioSceneShape } from '../../../../shape/shapes/general/root/studio-scene-shape';
import { StudioShape } from '../studio-shape';
import { RectangleElement } from '../../../../shape/shapes/primitive/rectangle-element';
import gsap from 'gsap';

export class StudioImageShape extends StudioShape<StudioImageShapeDescriptor> {
  private sprite: Sprite;
  private texture: Texture;

  container: Container;
  private _width: number;
  private _height: number;
  private _scale: { x: number; y: number } = { x: 1, y: 1 };
  private _translate: Translate = { x: 0, y: 0 };
  get s3Id() {
    return this.image.s3Id;
  }
  get cs() {
    return this.shape.cs;
  }
  set s3Id(val: string) {
    this.image.s3Id = val;
  }
  get cell() {
    return this.descriptor.cell;
  }
  getType(): string {
    return 'image-shape';
  }
  get rWidth() {
    return this._width * this._scale.x;
  }
  get rHeight() {
    return this._height * this._scale.y;
  }
  initialised;
  get image() {
    return (
      this.studioService.images[this.descriptor.imageId] ||
      this.studioService.images[this.id]
    );
  }

  get studioService() {
    return this.shape.studioService;
  }

  loadPromise: Promise<void> = Promise.resolve();

  maskRectangle: RectangleElement;
  constructor(
    shape: StudioSceneShape,
    id: string,
    data: StudioImageShapeDescriptor,
    public loaded?: () => void,
  ) {
    super(shape, id, data);
    this.loadPromise = this.init().then(() => {
      this.initRC();
      loaded?.();
    });
  }

  async init() {
    // console.log('init-image', this.image);
    this.container = new Container();
    const id = this.id;

    if (!this.image) {
      return;
    }
    // -- // -- // -- //
    const imageUrl = id?.startsWith('http')
      ? id
      : this.image.url || `https://private.cdn.inwedio.com/images/${id}`;

    this.initImagePreview(imageUrl);
    this.shape.container.addChild(this.container);
  }

  private initImagePreview(imageUrl: string) {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      this.createImageSprite(img);
      this.loaded?.();
    };

    img.onerror = () => {
      console.error('Failed to load image:', imageUrl);
      this.createPlaceholder();
    };

    img.src = imageUrl;
  }

  private createImageSprite(img: HTMLImageElement) {
    const whiteTexture = this.makeWhiteTexture(img);

    this.sprite = new Sprite(whiteTexture);
    this.container.addChild(this.sprite);
    // Create texture from image
    // this.texture = Texture.from(img);
    // this.sprite = new Sprite(this.texture);

    // this.container.addChild(this.sprite);

    // const white = new Graphics()
    //   .beginFill(0xffffff)
    //   .drawRect(0, 0, this.sprite.width, this.sprite.height)
    //   .endFill();

    // // Apply mask
    // white.mask = this.sprite;

    // this.container.addChild(white);

    // this.sprite.renderable = false; // or maskSprite.alpha = 0

    this._width = img.naturalWidth || 320;
    this._height = img.naturalHeight || 180;

    this.doAlign(this.cell);

    // Create mask rectangle after sprite is ready
    this.initMaskRectangle();

    this.container.visible = true;
    this.initialised = true;

    // Initialize rectangle controller after image is loaded
    this.initRC();
  }

  makeWhiteTexture(imgEl: HTMLImageElement) {
    const c = document.createElement('canvas');
    c.width = imgEl.naturalWidth;
    c.height = imgEl.naturalHeight;

    const ctx = c.getContext('2d')!;
    ctx.drawImage(imgEl, 0, 0);

    const im = ctx.getImageData(0, 0, c.width, c.height);
    const d = im.data;

    for (let i = 0; i < d.length; i += 4) {
      const a = d[i + 3];
      if (a !== 0) {
        d[i + 0] = 255;
        d[i + 1] = 255;
        d[i + 2] = 255;
      }
    }

    ctx.putImageData(im, 0, 0);

    return Texture.from(c);
  }

  initMaskRectangle() {
    if (this.maskRectangle) {
      this.maskRectangle.remove();
    }
    if (!this.sprite) {
      return;
    }
    const [width, height] = this.grossDims;
    this.maskRectangle = new RectangleElement(this, this.container, {
      width,
      height,
      fill: '#000000',
      opacity: 0.001,
      r: 24,
    });
    this.sprite.mask = this.maskRectangle.element;
  }

  private createPlaceholder() {
    // Create a simple placeholder graphic
    console.warn('Creating placeholder for failed image load');
    this._width = 320;
    this._height = 180;
    this.container.visible = true;
    this.initialised = true;
    // -- //
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  doAlign(cell: StudioCell, _params: AlignParameters = {}): Translate {
    if (!cell) {
      return;
    }

    this.coverAlign();
    return;
  }

  private coverAlign() {
    const width = this._width;
    const height = this._height;

    const [w, h] = this.netDims;
    const widthRatio = w / width;
    const heightRatio = h / height;

    const scale = Math.min(widthRatio, heightRatio);

    this._scale.x = scale;
    this._scale.y = scale;

    this.applyScale(this._scale);

    const [grossWidth, grossHeight] = this.grossDims;

    // console.log('---- image dx dy ----', dx, dy);
    const actualWidth = width * scale;
    const actualHeight = height * scale;
    this.sprite.position.set(
      (grossWidth - actualWidth) / 2,
      (grossHeight - actualHeight) / 2,
    );

    const [x0, y0] = this.grossCoords;
    this.container.position.set(x0, y0);
  }

  rc: RectangleController;
  private unmovedDims: [number, number];
  private draggedDims: [number, number];

  initRC() {
    if (!this.initialised) {
      console.warn('StudioImageShape not initialized yet, cannot create RC');
      return;
    }

    const [width, height] = this.grossDims;
    this.rc = new RectangleController(
      this as any,
      {
        id: `image-${this.descriptor.imageId}`,
        width,
        offset: [this.container.x, this.container.y],
        height,
        noStroke: false,
        fill: true,
        hiddenMode: true,
        noScale: false,
        fillOpacity: 0.001,
        'stroke-width': 1,
        startDrag: () => {
          this.unmovedDims = [width, height];
        },
        drag: resp => {
          const { width: newWidth, height: newHeight } = resp;
          this.draggedDims = [newWidth, newHeight];

          // Update image shape dimensions during drag
          if (this.cell) {
            const scaleX = newWidth / width;
            const scaleY = newHeight / height;

            this._scale.x *= scaleX;
            this._scale.y *= scaleY;
            this.applyScale(this._scale);
          }
        },
        endDrag: () => {
          if (!this.draggedDims || !this.unmovedDims) {
            return;
          }

          const [_width, _height] = this.unmovedDims;
          const [newWidth, newHeight] = this.draggedDims;

          // Reset dragged dims
          this.draggedDims = null;
        },
        clicked: () => this.onImageClicked(),
        mouseover: () => this.onMouseOver(),
        mouseout: () => this.onMouseOut(),
      },
      this.circleContainer,
      this.circleContainer,
    );

    // this.rc.rect.patch({
    //   stroke: '#0191f3',
    //   'stroke-width': 0,
    // });

    this.rc.show({ withoutPCs: true });
    this.onMouseOut();
  }

  update(descriptor: StudioImageShapeDescriptor) {
    this.descriptor = descriptor;
    // this.init().then(() => {
    console.log('update', this.descriptor);
    this.initMaskRectangle();
    this.coverAlign();
    // });
  }

  private onImageClicked() {
    console.log('StudioImageShape clicked:', this.descriptor.imageId);

    // Show rectangle controller
    this.rc?.show();

    // Dispatch selection event
    if (this.image) {
      this.shape.store.dispatch(
        selectMedia({
          id: this.image.id,
        }),
      );
    }
  }

  private onMouseOver() {
    if (this.rc) {
      this.rc.rect.patch({ 'stroke-width': 1 });
    }
  }

  private onMouseOut() {
    if (this.rc) {
      this.rc.rect.patch({ 'stroke-width': 0 });
    }
  }

  get dims() {
    return [this._width * this._scale.x, this._height * this._scale.y];
  }

  rcDrag(resp: DragResponse) {
    const { dx, dy } = resp;
    this._translate.x += dx;
    this._translate.y += dy;
    this.container.position.set(this._translate.x, this._translate.y);
  }

  endRCDrag() {
    // Save translate if needed
  }

  applyScale(scale: Scale) {
    this._scale = scale as _Scale;
    this.sprite.scale = scale as _Scale;
    // this.container.scale = scale as _Scale;
  }

  applyTranslate(translate: Translate) {
    this._translate = translate;
    this.container.position = translate;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  remove(_params?: { noSave?: boolean }) {
    if (this.sprite) {
      this.sprite.destroy();
      this.sprite = null;
    }
    if (this.texture) {
      this.texture.destroy();
      this.texture = null;
    }
    super.remove();
  }

  resetAnimation() {
    if (this.maskRectangle?.element) {
      gsap.killTweensOf(this.maskRectangle.element);
    }

    this.sprite.mask = null;
    this.maskRectangle?.remove();
    this.maskRectangle = null;
  }

  gsapAnimateStep(animation: StudioAnimation): gsap.core.Timeline {
    const timeline = gsap.timeline();

    if (!animation) {
      return timeline;
    }

    const { mask, zoom, duration, type, translate } = animation;
    const [width, height] = this.grossDims;

    if (type === 'appear') {
      if (!mask) {
        this.container.alpha = 0;
        timeline.add(gsap.to(this.container, { alpha: 1, duration }), 0);
      }
    }

    if (type === 'disappear') {
      timeline.add(gsap.to(this.container, { alpha: 0, duration }), 0);
    }

    if (zoom) {
      const { from, to, duration: zoomDuration, translate } = zoom;
      const [zoomDx, zoomDy] = translate || [0, 0];
      if (from !== undefined) {
        const currentWidth = this._width * this._scale.x;
        const currentHeight = this._height * this._scale.y;

        const { w, h } = this.cellDims;

        const dx = ((1 - from) * w) / 2;
        const dy = ((1 - from) * h) / 2;

        this.container.scale.x = from;
        this.container.scale.y = from;

        this.container.position.x += dx - zoomDx;
        this.container.position.y += dy - zoomDy;

        timeline.add(
          gsap.to(this.container.position, {
            duration: zoomDuration || duration || 1,
            x: (i, target) => {
              // const currentScale = this.container.scale.x as number;
              // const targetScale = to * currentScale;
              // const currentScale = this.sprite.scale.x as number;
              // const dx = ((from - to) * this._width * baseScale) / 2;
              const dx = ((from - to) * w) / 2;
              return target.x + dx + zoomDx;
            },
            y: (i, target) => {
              // const baseScale = this.container.scale.y as number;
              // const currentScale = this.sprite.scale.y as number;
              // const dy = ((currentScale - to) * this._height * baseScale) / 2;
              const dy = ((from - to) * h) / 2;
              return target.y + dy + zoomDy;
            },
            // ease: 'power1.inOut',
          }),
          0,
        );
      }

      if (to !== undefined) {
        timeline.add(
          gsap.to(this.container.scale, {
            duration: zoomDuration || duration || 1,
            x: to,
            y: to,
            // ease: 'power1.inOut',
          }),
          0,
        );
      }
    }

    if (translate) {
      const { from, to, duration: translateDuration } = translate;
      if (from) {
        const [x, y] = from;
        this.container.position.x += x;
        this.container.position.y += y;
      }

      if (from || to) {
        timeline.add(
          gsap.to(this.container.position, {
            duration: translateDuration || duration || 1,
            x: (i, target) => {
              return target.x + (to?.[0] || 0) - (from?.[0] || 0);
            },
            y: (i, target) => {
              return target.y + (to?.[1] || 0) - (from?.[1] || 0);
            },
            // ease: 'power1.inOut',
          }),
          0,
        );
      }
    }

    if (mask) {
      // -- //
      this.initMaskRectangle();
      this.maskRectangle?.patch({
        width: width,
        height: height,
        fill: '#000000',
        opacity: 0.001,
        r: 24,
      });

      let maskTween: gsap.core.Tween;
      switch (mask) {
        case 'left':
          this.maskRectangle.element.x = -width;
          maskTween = gsap.to(this.maskRectangle.element, {
            x: 0,
            duration: 0.4,
          });
          break;
        case 'right':
          this.maskRectangle.element.x = width;
          maskTween = gsap.to(this.maskRectangle.element, {
            x: 0,
            duration: 0.4,
          });
          break;
        case 'top':
          if (!this.maskRectangle) {
            return;
          }
          this.maskRectangle.element.y = -height;
          maskTween = gsap.to(this.maskRectangle.element, {
            y: 0,
            duration: 0.4,
          });
          break;
        case 'bottom':
          if (!this.maskRectangle) break;
          this.maskRectangle.element.y = height;
          maskTween = gsap.to(this.maskRectangle.element, {
            y: 0,
            duration: 0.4,
          });
          break;
      }

      if (maskTween) {
        timeline.add(maskTween, 0);
      }
      this.sprite.mask = this.maskRectangle.element;
    }

    return super.gsapAnimateStep(animation, timeline);
  }
}
